import * as React from 'react';
import { IPlayWeather } from '../../../../../interfaces/IPlay';
import * as styles from '../Weather.css';
import { IColorTheme } from '../../../../../interfaces/IColorTheme';
import { ColorTranslator } from 'colortranslator';
import { DaySection } from './DaySection';
import { TemperatureGraph } from './TemperatureGraph';

export interface IProps {
  widgetId: string;
  forecast: IPlayWeather[];
  width: number;
  height: number;
  sizeUnit: number;
  colorTheme: IColorTheme;
  isSmall?: boolean;
  getDaySections: () => DaySection[];
}

export interface IState {
  daySections: DaySection[];
  highestPrecipation: number;
}

export class DayHorizontalWeather extends React.Component<IProps, IState> {
  private readonly MARGIN_MULTIPLIER = 5;

  constructor(props: IProps) {
    super(props);
    this.state = {
      daySections: undefined,
      highestPrecipation: undefined
    };
  }

  componentDidMount(): void {
    let highestPrecipation = 0;
    // We split the day into sections (morning, noon, evening and night)
    const daySections = this.props.getDaySections();
    daySections.forEach(day => {
      if (day.weather.precipitationAmount > highestPrecipation) {
        highestPrecipation = day.weather.precipitationAmount;
      }
    })
    this.setState({
      daySections,
      highestPrecipation
    });
  }

  public render(): JSX.Element {
    const sizeMultiplier = this.props.isSmall ? 1 : 2;
    const colortranslator = new ColorTranslator(this.props.colorTheme.textColor.toLocaleLowerCase());
    const borderColor = `rgba(${colortranslator.R}, ${colortranslator.G}, ${colortranslator.B}, .1)`;
    const sectionTemperatures = [];
    if (this.state.daySections?.length >= 5) {
      this.state.daySections.slice(0, 5).map((section) => {
        sectionTemperatures.push(section.weather.airTemperature);
      });
    }
    return (
      <div
        // className={styles.IA_weatherWidgetContainer}
        style={{
          backgroundColor: this.props.colorTheme.backgroundColor,
          display: "flex",
          flexDirection: "row",
          height: "100%",
          width: "100%"
        }}
      >
        {this.state.daySections?.length >= 4 && this.state.daySections.slice(0, 4).map((section, i) => {
          const highestPrecipation = Math.max(this.state.highestPrecipation, 10);
          const daySectionHours = [
            { hour: section.startTime, date: section.startDate },
            { hour: section.startTime + 1, date: section.startDate },
            { hour: section.startTime + 2, date: section.startDate },
            { hour: section.startTime + 3, date: section.startDate },
            { hour: section.startTime + 4, date: section.startDate },
            { hour: section.startTime + 5, date: section.startDate }
          ];
          return <div
            // className={styles.IA_weatherDay}
            style={{
              marginTop: this.props.sizeUnit * this.MARGIN_MULTIPLIER,
              marginBottom: this.props.sizeUnit * this.MARGIN_MULTIPLIER * 2,
              marginLeft: i === 0 ? this.props.sizeUnit * this.MARGIN_MULTIPLIER : undefined,
              marginRight: i === 3 ? this.props.sizeUnit * this.MARGIN_MULTIPLIER : undefined,
              flex: 1,
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                borderRight: i !== 3 ? `${this.props.sizeUnit / 5}px solid ${borderColor}` : undefined,
                borderLeft: i !== 0 ? `${this.props.sizeUnit / 5}px solid ${borderColor}` : undefined,
                paddingBottom: this.props.isSmall ? this.props.sizeUnit * sizeMultiplier * 11 : this.props.sizeUnit * sizeMultiplier * 12
              }}
            >
              <p
                style={{
                  fontSize: this.props.sizeUnit * sizeMultiplier * 1.5,
                  marginBottom: this.props.sizeUnit * sizeMultiplier * 2,
                  marginTop: this.props.sizeUnit * sizeMultiplier * 2,
                  color: this.props.colorTheme.textColor
                }}
              >
                {section?.title?.toUpperCase()}
              </p>
              <div
                className={styles.IA_iconAndPrecipationContainer}
              >
                <img
                  src={section.weather.iconUrl}
                  className={styles.IA_weatherIcon}
                />
                <p
                  className={styles.IA_precipation}
                  style={{
                    fontSize: this.props.sizeUnit * sizeMultiplier,
                    minHeight: this.props.sizeUnit * sizeMultiplier * 1.5
                  }}
                >
                  {section.weather.precipitationAmount !== 0 ? `${Math.ceil(section.weather.precipitationAmount)} mm` : " "}
                </p>
              </div>
              <p
                style={{
                  marginTop: 0,
                  fontSize: this.props.sizeUnit * sizeMultiplier * 3.5,
                  color: this.props.colorTheme.textColor
                }}
              >
                {`${section.weather.airTemperature.toFixed(0)}°`}
              </p>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                flex: 1
              }}
            >
              {daySectionHours.map((section, j) => {
                let precipitation = this.props.forecast.find(weather => {
                  const date = new Date(weather.time);
                  return date.getHours() === section.hour && date.getDate() === section.date;
                })?.precipitationAmount ?? 0;
                const sectionHour = section?.hour;
                const sectionHourPlusOne = section?.hour + 1;
                return (
                  <div
                    style={{
                      flex: 1,
                      borderColor: borderColor,
                      borderRightWidth: i === 3 && j === 5 ? this.props.sizeUnit / 2.5 : this.props.sizeUnit / 5,
                      borderLeftWidth: i === 0 && j === 0 ? this.props.sizeUnit / 2.5 : this.props.sizeUnit / 5,
                      borderTopWidth: 0,
                      borderBottomWidth: 0,
                      borderStyle: "solid",
                      textAlign: "center",
                      padding: `0 ${this.props.sizeUnit / 2}px`
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "end"
                      }}
                    >
                      <div
                        style={{
                          height: `${precipitation / highestPrecipation * 100}%`,
                          backgroundColor: "#0062bf"
                        }}
                      />
                    </div>
                    <p
                      style={{
                        fontSize: this.props.sizeUnit * sizeMultiplier,
                        marginTop: this.props.sizeUnit,
                        color: this.props.colorTheme.textColor,
                        display: j % 2 === 0 ? "block" : "none",
                        position: "relative",
                        left: -this.props.sizeUnit * 1.75 * sizeMultiplier
                      }}
                    >
                      {sectionHour === 0 ? 24 : (sectionHour?.toString()?.length === 1 ? `0${sectionHour?.toString()}` : sectionHour?.toString())}
                    </p>
                    {daySectionHours.length - 1 === j && i === 3 &&
                      <p
                        style={{
                          fontSize: this.props.sizeUnit * sizeMultiplier,
                          marginTop: this.props.sizeUnit,
                          color: this.props.colorTheme.textColor,
                          display: j % 2 !== 0 ? "block" : "none",
                          position: "relative",
                          right: -this.props.sizeUnit * 1.75 * sizeMultiplier
                        }}
                      >
                        {sectionHourPlusOne === 0 ? 24 : (sectionHourPlusOne?.toString()?.length === 1 ? `0${sectionHourPlusOne?.toString()}` : sectionHourPlusOne?.toString())}
                      </p>
                    }
                  </div>
                )
              })}
            </div>
          </div>
        })}
        <TemperatureGraph
          widgetId={this.props.widgetId}
          temperatures={sectionTemperatures}
          sizeUnit={this.props.sizeUnit}
          isSmall={this.props.isSmall}
          marginMultiplier={this.MARGIN_MULTIPLIER}
          width={this.props.width}
          color={this.props.colorTheme.textColor}
        />
      </div>
    );
  }
}